import { WeaveIdleDetector } from '@frontend/flow';
import { type AuthMethod, type MfaMethod, MAX_TIME_TO_AUTH_MS, recordTimeToAuth } from './auth-metrics';

/**
 * No input for this long counts as idle (same threshold the shared Flow idle detector uses). Only the time past
 * the threshold is counted as idle, and hidden-tab time isn't idle (e.g. switching to email for an MFA code).
 */
export const AUTH_IDLE_THRESHOLD_MS = 30_000;

export type AuthAttemptsBucket = '1' | '2' | '3+';

export interface AuthFlowTiming {
  /** active time: total time minus idle time */
  durationMs: number;
  totalMs: number;
  idleMs: number;
  /** the tab was hidden at some point while it was on a gatekeeper page */
  backgrounded: boolean;
  attempts: AuthAttemptsBucket;
}

/** What survives page loads (IdP round trips, flow restarts). Times are epoch ms. */
interface StoredFlow {
  startedAt: number;
  idleMs: number;
  backgrounded: boolean;
  failedAttempts: number;
}

const STORAGE_KEY = 'auth-metrics:flow';

/** Epoch-based and monotonic within a page load, so it can be compared with times recorded on other pages. */
const now = () => performance.timeOrigin + performance.now();

const isUserLooking = () => document.visibilityState === 'visible' && document.hasFocus();

const isStoredFlow = (value: unknown): value is StoredFlow =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as StoredFlow).startedAt === 'number' &&
  typeof (value as StoredFlow).idleMs === 'number' &&
  typeof (value as StoredFlow).failedAttempts === 'number';

// storage can be unavailable (e.g. blocked by browser settings); timing is best-effort, so fail quietly
const readFlow = (): StoredFlow | undefined => {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null');
    return isStoredFlow(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const writeFlow = (flow: StoredFlow) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(flow));
  } catch {
    // see readFlow
  }
};

const clearFlow = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // see readFlow
  }
};

const bucketAttempts = (failedAttempts: number): AuthAttemptsBucket =>
  failedAttempts === 0 ? '1' : failedAttempts === 1 ? '2' : '3+';

/**
 * Real-user timing for a whole login, from when the user can first see gatekeeper until hydra accepts the login,
 * including their own time (typing, retries, fetching an MFA code, signing in at an SSO IdP).
 *
 * A flow can span page loads (SSO/Okta IdP round trips, restarts with a new login challenge), so its state lives
 * in sessionStorage: it's started or resumed wherever a login can begin (the login form, the SSO tenant page) and
 * finished wherever one completes (the login form, the SSO/Okta callbacks). A flow older than
 * `MAX_TIME_TO_AUTH_MS` is abandoned and a new one starts.
 *
 * If the page is visible and focused when a flow starts, it's timed from navigation start, so page load counts.
 * Otherwise (a tab opened in the background) it starts when the user first looks at it.
 */
export class AuthFlowTimer {
  private idleStartedAt: number | null = null;
  private cleanups: (() => void)[] = [];

  /** Start a flow, or resume the one in progress, and track idle/background time while this page is open. */
  start(): void {
    if (this.cleanups.length > 0) return; // already running on this page

    const existing = readFlow();
    if (existing && now() - existing.startedAt <= MAX_TIME_TO_AUTH_MS) {
      this.watch();
      return;
    }
    clearFlow();

    if (isUserLooking()) {
      this.begin(performance.timeOrigin); // navigation start
      return;
    }
    const beginWhenLooking = () => {
      if (!isUserLooking()) return;
      this.detach();
      this.begin(now());
    };
    window.addEventListener('focus', beginWhenLooking);
    document.addEventListener('visibilitychange', beginWhenLooking);
    this.cleanups.push(() => {
      window.removeEventListener('focus', beginWhenLooking);
      document.removeEventListener('visibilitychange', beginWhenLooking);
    });
  }

  /** A credential attempt that didn't get the user in (wrong password, bad MFA code, failed SSO callback...). */
  recordFailedAttempt(): void {
    const flow = readFlow();
    if (flow) writeFlow({ ...flow, failedAttempts: flow.failedAttempts + 1 });
  }

  /** End the flow and return its timing, or undefined if there's no flow in progress. */
  finish(): AuthFlowTiming | undefined {
    this.persistIdle();
    const flow = readFlow();
    this.cancel();
    if (!flow) return undefined;

    const totalMs = now() - flow.startedAt;
    return {
      durationMs: totalMs - flow.idleMs,
      totalMs,
      idleMs: flow.idleMs,
      backgrounded: flow.backgrounded,
      attempts: bucketAttempts(flow.failedAttempts),
    };
  }

  /** End the flow without reporting it, e.g. when the user was already signed in and never had to log in. */
  cancel(): void {
    this.detach();
    clearFlow();
  }

  /** Stop tracking on this page but keep the flow going, e.g. when navigating away mid-flow. */
  detach(): void {
    this.persistIdle();
    this.cleanups.forEach((cleanup) => cleanup());
    this.cleanups = [];
  }

  private begin(startedAt: number) {
    writeFlow({ startedAt, idleMs: 0, backgrounded: false, failedAttempts: 0 });
    this.watch();
  }

  private watch() {
    const idleDetector = new WeaveIdleDetector({ threshold: AUTH_IDLE_THRESHOLD_MS });
    this.cleanups.push(
      idleDetector.onChange((state) => {
        if (state === 'idle' && this.idleStartedAt === null) {
          this.idleStartedAt = now();
        } else if (state === 'active') {
          this.persistIdle();
        }
      })
    );

    // leaving the page (e.g. redirecting to an IdP) also makes it hidden, right after pagehide -- that's not the
    // user backgrounding the tab
    let isLeavingPage = false;
    const onVisibilityChange = () => {
      const flow = readFlow();
      if (document.visibilityState === 'hidden' && !isLeavingPage && flow && !flow.backgrounded) {
        writeFlow({ ...flow, backgrounded: true });
      }
    };
    // a full-page navigation doesn't unmount anything, so also save idle time on the way out
    const onPageHide = () => {
      isLeavingPage = true;
      this.persistIdle();
    };
    // back/forward cache: the page came back without reloading
    const onPageShow = () => {
      isLeavingPage = false;
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    this.cleanups.push(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    });
  }

  /** Fold an idle stretch in progress into the stored flow. */
  private persistIdle() {
    if (this.idleStartedAt === null) return;
    const idleMs = now() - this.idleStartedAt;
    this.idleStartedAt = null;
    const flow = readFlow();
    if (flow) writeFlow({ ...flow, idleMs: flow.idleMs + idleMs });
  }
}

/** One per page: a flow's start, attempts, and finish happen in different components (and pages). */
export const authFlowTimer = new AuthFlowTimer();

/** Finish the login flow and record its one `auth.time_to_auth` sample. */
export function recordAuthFlowSuccess(
  method: AuthMethod,
  { mfa, mfaMethod }: { mfa?: boolean; mfaMethod?: MfaMethod } = {}
): void {
  const timing = authFlowTimer.finish();
  recordTimeToAuth({
    durationMs: timing?.durationMs,
    method,
    outcome: 'success',
    mfa,
    mfaMethod,
    attempts: timing?.attempts,
    backgrounded: timing?.backgrounded,
    idle: timing ? timing.idleMs > 0 : undefined,
  });
}
